/** Form definition model (spec/form-engine/form-definition.schema.json). */
export type FieldType =
  | 'TEXT'
  | 'TEXTAREA'
  | 'RICH_TEXT'
  | 'TABLE'
  | 'NUMBER'
  | 'DATE'
  | 'SINGLE_CHOICE'
  | 'MULTI_CHOICE'
  | 'YES_NO'
  | 'TEAM_MEMBER'
  | 'AUTOCOMPLETE'
  | 'SWITCH'
  | 'SECTION'
  | 'REPEATING_GROUP'
  | 'INFO';

export const FIELD_TYPES: FieldType[] = [
  'TEXT',
  'TEXTAREA',
  'RICH_TEXT',
  'TABLE',
  'NUMBER',
  'DATE',
  'SINGLE_CHOICE',
  'MULTI_CHOICE',
  'YES_NO',
  'TEAM_MEMBER',
  'AUTOCOMPLETE',
  'SWITCH',
  'SECTION',
  'REPEATING_GROUP',
  'INFO',
];

export const SIMPLE_VALUE_TYPES: FieldType[] = ['TEXT', 'TEXTAREA', 'NUMBER', 'DATE', 'SINGLE_CHOICE', 'MULTI_CHOICE', 'YES_NO', 'TEAM_MEMBER', 'AUTOCOMPLETE', 'SWITCH'];

export function hasValue(type: FieldType): boolean {
  return type !== 'SECTION' && type !== 'INFO';
}

/** Fields that can have a validation rule (validWhen): those with a simple value. */
export function canHaveValidation(type: FieldType): boolean {
  return SIMPLE_VALUE_TYPES.includes(type);
}

/** Fields an administrator can show in a chart (D31): a TABLE, a repeating group or a section. */
export function canHaveChart(type: FieldType): boolean {
  return type === 'TABLE' || type === 'REPEATING_GROUP' || type === 'SECTION';
}

/** A SWITCH always has a state while active, so `required` does not apply (like SECTION and INFO). */
export function canBeRequired(type: FieldType): boolean {
  return type !== 'SECTION' && type !== 'INFO' && type !== 'SWITCH';
}

/** AUTOCOMPLETE sources: the register searched for suggestions; the value is the id of the chosen record. */
export type AutocompleteSource = 'SUBJECT' | 'INSPECTOR';

export interface LocalizedText {
  sk: string;
  en?: string;
}

export interface OptionDef {
  value: string;
  label: LocalizedText;
  /** the option is offered only while this holds; a chosen option that is not offered is dropped (D23) */
  visibleWhen?: Condition;
}

/** A column (first row) or a row (first column) header of a TABLE; the key holds its cells in the value. */
export interface TableHeader {
  key: string;
  label: LocalizedText;
}

/** TABLE: the corner header, the column headers and the row headers; every other cell is a text input. */
export interface TableDef {
  corner: LocalizedText;
  columns: TableHeader[];
  rows: TableHeader[];
}

export interface Constraints {
  maxLength?: number;
  format?: 'none' | 'email' | 'phone' | 'ico' | 'psc';
  rows?: number;
  min?: string;
  max?: string;
  decimals?: number;
  relative?: 'WITHIN_TIMEFRAME' | 'ON_OR_BEFORE_START' | 'ON_OR_AFTER_TODAY';
  /** SINGLE_CHOICE: radios (default) | select; MULTI_CHOICE: checkboxes (default) | select */
  display?: 'radios' | 'checkboxes' | 'select';
  minSelected?: number;
  maxSelected?: number;
  minRows?: number;
  maxRows?: number;
  variant?: 'inset' | 'warning';
  /** AUTOCOMPLETE, default SUBJECT */
  source?: AutocompleteSource;
}

export type Operator = 'eq' | 'neq' | 'in' | 'notIn' | 'includes' | 'excludes' | 'empty' | 'notEmpty' | 'gt' | 'gte' | 'lt' | 'lte';
export const OPERATORS: Operator[] = ['eq', 'neq', 'in', 'notIn', 'includes', 'excludes', 'empty', 'notEmpty', 'gt', 'gte', 'lt', 'lte'];

export interface LeafCondition {
  field: string;
  op: Operator;
  value?: JsonValue;
  /** compare with the value of this field (a reference like `field`) instead of a fixed value (D26) */
  valueField?: string;
}

/** The operators that may compare with the value of another field (valueField). */
export const FIELD_COMPARISONS: Operator[] = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'];

export type Condition = { all: Condition[] } | { any: Condition[] } | { not: Condition } | LeafCondition;

export type RuleName = 'visibleWhen' | 'enabledWhen' | 'requiredWhen' | 'validWhen';

export interface FieldDef {
  key: string;
  type: FieldType;
  label: LocalizedText;
  hint?: LocalizedText;
  required?: boolean;
  constraints?: Constraints;
  options?: OptionDef[];
  table?: TableDef;
  width?: string;
  visibleWhen?: Condition;
  enabledWhen?: Condition;
  requiredWhen?: Condition;
  /** the value is valid only while this holds; it may refer to the field itself (D26) */
  validWhen?: Condition;
  /** the error shown when validWhen does not hold (else one made of the rule in words) */
  validationMessage?: LocalizedText;
  origin?: 'TEMPLATE' | 'INSPECTION';
  placeAfter?: string | null;
  children?: FieldDef[];
  /** show the numbers of this TABLE, repeating group or section in a chart of the finished inspection (D31) */
  chart?: boolean;
}

export interface FormDefinition {
  fields: FieldDef[];
}

export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };
export type FormValues = Record<string, JsonValue>;

export interface TeamMember {
  id: string;
  name: string;
}

export interface EvaluationContext {
  /** ISO date (Europe/Bratislava). */
  today?: string;
  timeframe?: { from: string; to: string } | null;
  teamMembers?: TeamMember[] | null;
}

export interface FieldState {
  visible: boolean;
  enabled: boolean;
  required: boolean;
  /** choice fields with option rules: the values of the options offered now */
  options?: string[];
}

export interface FieldError {
  field: string;
  code: string;
  params?: Record<string, string | number>;
}

export interface EvaluationResult {
  state: Record<string, FieldState>;
  values: FormValues;
  errors: FieldError[];
  complete: boolean;
}

/** Errors meaning "not filled in yet" (allowed in a saved draft). */
export const MISSING_CODES = new Set(['required', 'rows.min', 'choice.minSelected', 'table.incomplete']);

export function isLeaf(c: Condition): c is LeafCondition {
  return 'field' in c;
}

export function stateLabel(s: FieldState): 'hidden' | 'disabled' | 'optional' | 'required' {
  if (!s.visible) return 'hidden';
  if (!s.enabled) return 'disabled';
  return s.required ? 'required' : 'optional';
}

export function text(t: LocalizedText | undefined, lang: string): string {
  if (!t) return '';
  return lang === 'en' && t.en && t.en.trim() ? t.en : t.sk;
}

/** Whether the text in `lang` falls back to Slovak (rendered with lang="sk", WCAG 3.1.2). */
export function fallsBackToSlovak(t: LocalizedText | undefined, lang: string): boolean {
  return Boolean(t) && lang === 'en' && !(t!.en && t!.en.trim());
}
